import { useCallback, useState } from "react";

/**
 * Persist pagination page-size per list key in localStorage.
 * Falls back to `defaultSize` when missing/invalid.
 */
export function usePersistedPageSize(
  storageKey: string,
  defaultSize: number,
  allowedSizes?: number[],
): [number, (next: number) => void] {
  const [itemsPerPage, setItemsPerPageState] = useState(() => {
    if (typeof window === "undefined") return defaultSize;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return defaultSize;
      const parsed = Number.parseInt(raw, 10);
      if (!Number.isFinite(parsed) || parsed <= 0) return defaultSize;
      if (allowedSizes && !allowedSizes.includes(parsed)) return defaultSize;
      return parsed;
    } catch {
      return defaultSize;
    }
  });

  const setItemsPerPage = useCallback(
    (next: number) => {
      setItemsPerPageState(next);
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        // ignore quota / private mode
      }
    },
    [storageKey],
  );

  return [itemsPerPage, setItemsPerPage];
}
